import { httpClient } from "./client";
import type { GuardianLinkApi } from "./interfaces";
import type {
  ChildGuardianshipResponse,
  GuardianActivityRequestCreate,
  GuardianActivityRequestView,
  GuardianControlName,
  GuardianControlState,
  GuardianControlView,
  GuardianLinkResponse,
  GuardianPermissionView,
  MyGuardianControlsResponse,
} from "../types/generated";
import { isApiError } from "./errors";

/**
 * Guardian-link structured error codes — the SINGLE SOURCE OF TRUTH the FE
 * branches on (#1309 / #1626 parent-managed under-13 consent).
 *
 * These MUST equal the codes the backend's exception handlers emit. The backend
 * registers them in `backend/main.py` via
 * `app.add_exception_handler(<Error>, _make_structured_handler(<status>, "<CODE>"))`
 * (the parental-consent / guardian-link block). `httpClient` copies the response
 * `detail.code` VERBATIM into `ApiError.code`, so any drift between these literals
 * and the backend silently breaks every inline error message (the original bug:
 * the child panel branched on `SELF_LINK_NOT_ALLOWED` / `DUPLICATE_LINK`, which the
 * backend never emits — so a self-link showed the generic copy and every Resend
 * looked like a failure).
 *
 * GuardianLinkStep (child) + FamilyApprovalsPage (parent) reference these instead
 * of inline string literals; `guardian-link-error-codes.contract.test.ts` asserts
 * every value here is a code the backend actually registers, so FE/BE drift fails CI.
 */
export const GUARDIAN_LINK_ERROR_CODES = {
  /** Child entered their OWN email as the parent's (400). */
  SELF_NOT_ALLOWED: "GUARDIAN_LINK_SELF_NOT_ALLOWED",
  /** A non-revoked link already exists for this child (409) — incl. Resend. */
  ALREADY_EXISTS: "GUARDIAN_LINK_ALREADY_EXISTS",
  /** The link id no longer resolves — already consumed/revoked (404). */
  NOT_FOUND: "GUARDIAN_LINK_NOT_FOUND",
  /** The acting parent has not verified their account email (403). */
  EMAIL_NOT_VERIFIED: "CONSENT_EMAIL_NOT_VERIFIED",
  /** The token caller is not the parent named on the request (403). */
  NOT_THE_PARENT: "NOT_THE_PARENT",
  /** The child no longer needs parental approval (400). */
  CHILD_NOT_PENDING: "CHILD_NOT_PENDING",
  /** Multi-guardian (#2691): the caller is not a current guardian of this child (403). */
  NOT_A_GUARDIAN: "NOT_A_GUARDIAN",
  /** A child with a guardian cannot start another link; a guardian invites (403). */
  BOOTSTRAP_CLOSED: "GUARDIAN_LINK_BOOTSTRAP_CLOSED",
  /** The child already has the maximum number of guardians (409). */
  LIMIT_REACHED: "GUARDIAN_LIMIT_REACHED",
  /** A co-guardian changed the permission first; reload before granting (409). */
  PERMISSION_CHANGED: "GUARDIAN_PERMISSION_CHANGED",
  /** Guardians must be adults and never the child themselves (403). */
  MUST_BE_ADULT: "GUARDIAN_MUST_BE_ADULT",
  /** The guardian must confirm their date of birth first (409). */
  DOB_REQUIRED: "GUARDIAN_DOB_REQUIRED",
  /** Too many link requests or invites for this child today (429). */
  RATE_LIMITED: "GUARDIAN_LINK_RATE_LIMITED",
  /** The invited email goes to the child's own inbox (403). */
  INVITE_IS_CHILD: "GUARDIAN_INVITE_IS_CHILD",
  /** The child's own email couldn't be checked, so the invite failed closed (403). */
  INVITE_CHECK_UNAVAILABLE: "GUARDIAN_INVITE_CHECK_UNAVAILABLE",
} as const;

export type GuardianLinkErrorCode =
  (typeof GUARDIAN_LINK_ERROR_CODES)[keyof typeof GUARDIAN_LINK_ERROR_CODES];

/**
 * Guardian activity-control codes (#2691). The three 403 codes come from ONE
 * backend handler (`GuardianControlDenied` in backend/main.py) on every
 * controlled action; the rest are the request/control routes' own errors.
 */
export const GUARDIAN_CONTROL_ERROR_CODES = {
  /** A minor with no active guardian, or a paused account: look-only mode (403). */
  REQUIRED: "GUARDIAN_REQUIRED",
  /** A guardian set this activity to Decline (403). */
  DECLINED: "GUARDIAN_DECLINED",
  /** Ask: a guardian must approve this person or place first (403). */
  APPROVAL_REQUIRED: "GUARDIAN_APPROVAL_REQUIRED",
  /** Someone else this action admits needs a guardian's approval; never says who (403). */
  MEMBER_APPROVAL_REQUIRED: "GUARDIAN_MEMBER_APPROVAL_REQUIRED",
  /** Another guardian already decided this request (409). */
  REQUEST_DECIDED: "GUARDIAN_REQUEST_DECIDED",
  /** The request no longer exists for this caller (404). */
  REQUEST_NOT_FOUND: "GUARDIAN_REQUEST_NOT_FOUND",
  /** Already allowed, or nothing to ask about (409). */
  REQUEST_NOT_NEEDED: "GUARDIAN_REQUEST_NOT_NEEDED",
  /** The person or place can't be asked about any more (404). */
  REQUEST_SUBJECT_UNAVAILABLE: "GUARDIAN_REQUEST_SUBJECT_UNAVAILABLE",
  /** Too many requests today (429). */
  REQUEST_RATE_LIMITED: "GUARDIAN_REQUEST_RATE_LIMITED",
  /** That setting isn't offered yet (404). */
  CONTROL_UNAVAILABLE: "GUARDIAN_CONTROL_UNAVAILABLE",
  /** That choice isn't offered for the setting (422). */
  CONTROL_CHOICE_INVALID: "GUARDIAN_CONTROL_CHOICE_INVALID",
} as const;

export type GuardianControlErrorCode =
  (typeof GUARDIAN_CONTROL_ERROR_CODES)[keyof typeof GUARDIAN_CONTROL_ERROR_CODES];

/** What a guardian 403 says the child can do next. */
export interface GuardianDenial {
  /** MEMBER_APPROVAL_REQUIRED: someone else the action adds (a party member, a
   * player being checked in) needs their own guardian's approval first. */
  code:
    | "GUARDIAN_REQUIRED"
    | "GUARDIAN_DECLINED"
    | "GUARDIAN_APPROVAL_REQUIRED"
    | "GUARDIAN_MEMBER_APPROVAL_REQUIRED";
  control: GuardianControlName | null;
  /** Present only when a guardian can be asked about this exact person or place. */
  subject: { kind: GuardianActivityRequestCreate["subject_kind"]; id: string } | null;
  /** The request already waiting for a guardian, if any. */
  requestId: string | null;
}

const DENIAL_CODES: ReadonlySet<string> = new Set([
  GUARDIAN_CONTROL_ERROR_CODES.REQUIRED,
  GUARDIAN_CONTROL_ERROR_CODES.DECLINED,
  GUARDIAN_CONTROL_ERROR_CODES.APPROVAL_REQUIRED,
  GUARDIAN_CONTROL_ERROR_CODES.MEMBER_APPROVAL_REQUIRED,
]);

/** Read a guardian-control 403 off any error; null for every other error. */
export function guardianDenialFromError(error: unknown): GuardianDenial | null {
  if (!isApiError(error) || error.status !== 403 || !DENIAL_CODES.has(error.code)) {
    return null;
  }
  const details = (error.details ?? {}) as Record<string, unknown>;
  const text = (value: unknown) => (typeof value === "string" && value ? value : null);
  const kind = text(details.subject_kind);
  const id = text(details.subject_id);
  return {
    code: error.code as GuardianDenial["code"],
    control: text(details.control) as GuardianControlName | null,
    subject:
      kind && id
        ? { kind: kind as GuardianActivityRequestCreate["subject_kind"], id }
        : null,
    requestId: text(details.request_id),
  };
}

/**
 * Guardian-link client — parent-managed under-13 consent (COPPA, #1309 Part A).
 *
 * The guardian-link router is mounted on the `/users` prefix
 * (`backend/app/routers/parental_consent.py`). httpClient prefixes every path
 * with `/bff/api`, so the BFF sees `/bff/api/users/me/...`.
 *
 *   CHILD (gated under-13 player):
 *     requestGuardianLink(parentEmail) → POST /users/me/guardian-link
 *                                          body { parent_email } → GuardianLinkResponse
 *     getLinkStatus()                  → GET  /users/me/guardian-link
 *                                          → GuardianLinkResponse | null
 *
 *   PARENT (authenticated BW account holder):
 *     listChildApprovals()  → GET  /users/me/child-approvals → GuardianLinkResponse[] (pending)
 *     listActiveChildren()  → GET  /users/me/child-approvals?status=active → GuardianLinkResponse[]
 *     approveChild(linkId)  → POST /users/me/child-approvals/{id}/approve
 *     revokeChild(linkId)   → POST /users/me/child-approvals/{id}/revoke
 *
 * The child supplies ONLY the parent's email — child_user_id is the token caller,
 * server-derived, never in the body. approve/revoke carry NO body (the link_id is
 * in the path; the actor is the token parent). The link_id path segment is
 * URL-encoded. There is NO Stripe/charge surface here — the parent approves from
 * their own authenticated account (public-profile unlock is a separate slice,
 * #1624).
 */
export const guardianLinkApi: GuardianLinkApi = {
  requestGuardianLink: (parentEmail: string) =>
    httpClient.post<GuardianLinkResponse>("/users/me/guardian-link", {
      parent_email: parentEmail,
    }),

  getLinkStatus: () =>
    httpClient.get<GuardianLinkResponse | null>("/users/me/guardian-link"),

  listChildApprovals: () =>
    httpClient.get<GuardianLinkResponse[]>("/users/me/child-approvals"),

  listActiveChildren: () =>
    httpClient.get<GuardianLinkResponse[]>(
      "/users/me/child-approvals?status=active",
    ),

  approveChild: (linkId: string) =>
    httpClient.post<GuardianLinkResponse>(
      `/users/me/child-approvals/${encodeURIComponent(linkId)}/approve`,
    ),

  revokeChild: (linkId: string) =>
    httpClient.post<GuardianLinkResponse>(
      `/users/me/child-approvals/${encodeURIComponent(linkId)}/revoke`,
    ),

  // Multi-guardian (#2691). `linkId` is always the CALLER's own guardian link,
  // so no request ever carries a child id.
  getChildGuardianship: (linkId: string) =>
    httpClient.get<ChildGuardianshipResponse>(
      `/users/me/family/children/${encodeURIComponent(linkId)}`,
    ),

  inviteCoGuardian: (linkId: string, email: string) =>
    httpClient.post<GuardianLinkResponse>(
      `/users/me/family/children/${encodeURIComponent(linkId)}/guardians`,
      { email },
    ),

  leaveGuardianship: (linkId: string) =>
    httpClient.post<GuardianLinkResponse>(
      `/users/me/family/children/${encodeURIComponent(linkId)}/leave`,
    ),

  grantPermission: (linkId: string, permission: "account_active", expectedVersion: number) =>
    httpClient.post<GuardianPermissionView>(
      `/users/me/family/children/${encodeURIComponent(linkId)}/permissions/${permission}/grant`,
      { expected_version: expectedVersion },
    ),

  revokePermission: (linkId: string, permission: "account_active") =>
    httpClient.post<GuardianPermissionView>(
      `/users/me/family/children/${encodeURIComponent(linkId)}/permissions/${permission}/revoke`,
    ),

  // Guardian activity controls (#2691).
  setControl: (
    linkId: string,
    control: GuardianControlName,
    state: GuardianControlState,
    expectedVersion: number,
  ) =>
    httpClient.put<GuardianControlView>(
      `/users/me/family/children/${encodeURIComponent(linkId)}/controls/${encodeURIComponent(control)}`,
      { state, expected_version: expectedVersion },
    ),

  getMyControls: () =>
    httpClient.get<MyGuardianControlsResponse>("/users/me/guardian-controls"),

  createRequest: (data: GuardianActivityRequestCreate) =>
    httpClient.post<GuardianActivityRequestView>("/users/me/guardian-requests", data),

  listMyRequests: () =>
    httpClient.get<GuardianActivityRequestView[]>("/users/me/guardian-requests"),

  withdrawRequest: (requestId: string) =>
    httpClient.post<GuardianActivityRequestView>(
      `/users/me/guardian-requests/${encodeURIComponent(requestId)}/withdraw`,
    ),

  listFamilyRequests: (status: "pending" | "approved" | "all" = "pending") =>
    httpClient.get<GuardianActivityRequestView[]>(
      `/users/me/family/requests?status=${status}`,
    ),

  decideRequest: (requestId: string, decision: "approve" | "decline" | "revoke") =>
    httpClient.post<GuardianActivityRequestView>(
      `/users/me/family/requests/${encodeURIComponent(requestId)}/${decision}`,
    ),
};
